import { Button, DatePicker, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
const { Option } = Select;
const { TextArea } = Input;

import ImageListUploader from '../image-upload/ImageListUploader';
import MainImageUploader from '../image-upload/MainImageUploader';
import { PlusCircleOutlined } from '@ant-design/icons';
import DocumentUploader from '../image-upload/DocumentUploader';
import { useTranslation } from 'react-i18next';
import { getLocalizedTitle } from '../../utils/localization';
import TranslatableInput from './TranslatableInput';

export const getFormItems = (dropdowns, hasWritePermission = false) => {
  const { t } = useTranslation();

  const getDropdownOptions = (type) => {
    if (dropdowns) {
      return dropdowns[type];
    }
    return [];
  };

  const getSelectFormItem = (type, label, rootFieldName, multiple = false, disabled = false) => {
    return (
      <Form.Item label={label} name={[rootFieldName, type]}>
        <Select
          showSearch
          filterOption
          optionFilterProp="children"
          placeholder={`${t("common.select")} ${label || type}`}
          mode={multiple ? 'multiple' : false}
          disabled={!hasWritePermission || disabled}
          allowClear
        >
          {
            getDropdownOptions(type)?.map(option => (
              <Option value={option.id} key={option.id}>{getLocalizedTitle(option)}</Option>
            ))
          }
        </Select>
      </Form.Item>
    );
  };

  const getParentSelectFormItem = (type, label, setChildren, disabled = false) => {
    const [selected, setSelected] = useState();
    return (
      <Form.Item label={label} name={['dropdowns', type]}>
        <Select
          value={selected}
          onSelect={value => {
            setSelected(value);
            let found = getDropdownOptions(type).find(dd => dd.id === value);
            if (found) {
              setChildren(found.children);
            }
          }}
          onClear={() => {
            setSelected(null);
            setChildren([]);
          }}
          showSearch
          filterOption
          optionFilterProp="children"
          placeholder={`${t("common.select")} ${label || type}`}
          disabled={!hasWritePermission || disabled}
          allowClear
        >
          {
            getDropdownOptions(type)?.map(option => (
              <Option value={option.id} key={option.id}>{getLocalizedTitle(option)}</Option>
            ))
          }
        </Select>
      </Form.Item>
    );
  };

  const getChildSelectFormItem = (type, label, children, disabled = false, onSelect = () => { }) => {
    return (
      <Form.Item label={label} name={['dropdowns', type]}>
        <Select
          showSearch
          filterOption
          optionFilterProp="children"
          placeholder={`${t("common.select")} ${label || type}`}
          disabled={!hasWritePermission || disabled}
          onSelect={onSelect}
          allowClear
        >
          {
            children && children.map(option => (
              <Option value={option.id} key={option.id}>{getLocalizedTitle(option)}</Option>
            ))
          }
        </Select>
      </Form.Item>
    );
  };

  const getDropdownFormItem = (type, label, disabled = false) => {
    return getSelectFormItem(type, label, 'dropdowns', false, disabled);
  };

  const getMultiselectFormItem = (type, label, disabled = false) => {
    return getSelectFormItem(type, label, 'multiSelects', true, disabled);
  };

  const getInputFormItem = (name, label, required = false, type = 'text', disabled = false) => {
    return (
      <Form.Item
        label={label}
        name={name}
        rules={required ? [{ required: true, message: `${label} ${t('formBuilder.isRequired')}` }] : []}
      >
        <Input disabled={!hasWritePermission || disabled} placeholder={label} type={type} allowClear />
      </Form.Item>
    );
  }

  const getInputNumberFormItem = (name, label, addon, disabled = false, required = false) => {
    return (
      <Form.Item
        label={label}
        name={name}
        rules={required ? [{ required: true, message: `${label} ${t('formBuilder.isRequired')}` }] : []}
      >
        <InputNumber disabled={!hasWritePermission || disabled} addonAfter={addon} />
      </Form.Item>
    );
  }

  const getDatePickerFormItem = (name, label, style, disabled = false) => (
    <Form.Item
      label={label}
      name={name}
    >
      <DatePicker disabled={!hasWritePermission || disabled} format="YYYY/MM/DD" style={style} />
    </Form.Item>
  )

  const getImageListFormItem = (name, disabledAdd, storageFolder, fileList, setFileList, maxImageSize = 20) => (
    <Form.Item
      name={name}
      noStyle
    >
      <div>
        <ImageListUploader
          disabledAdd={!hasWritePermission || disabledAdd}
          storageFolder={storageFolder}
          maxImageSize={maxImageSize}
          fileList={fileList}
          setFileList={setFileList}
        />
      </div>
    </Form.Item>
  )

  const getMainImageFormItem = (name, disabledAdd, storageFolder, fileList, setFileList) => (
    <Form.Item
      name={name}
      noStyle
    >
      <div>
        <MainImageUploader
          disabledAdd={!hasWritePermission || disabledAdd}
          storageFolder={storageFolder}
          fileList={fileList}
          setFileList={setFileList}
        />
      </div>
    </Form.Item>
  );

  const getTextAreaFormItem = (name, label, wrapperCol, disabled = false, maxLength = 240, rows = undefined) => (
    <Form.Item
      label={label}
      name={name}
      wrapperCol={wrapperCol}
    >
      <TextArea
        disabled={!hasWritePermission || disabled}
        placeholder={label}
        showCount
        allowClear
        maxLength={maxLength}
        rows={rows}
      />
    </Form.Item>
  )

  const getTextAreaFormItemWoLength = (name, label, wrapperCol, disabled = false, rows = undefined) => (
    <Form.Item
      label={label}
      name={name}
      wrapperCol={wrapperCol}
    >
      <TextArea
        disabled={!hasWritePermission || disabled}
        placeholder={label}
        showCount
        allowClear
        rows={rows}
      />
    </Form.Item>
  )

  const getTranslatableFormItem = (name, label, multiline = false, maxLength, disabled = false, wrapperCol) => (
    <Form.Item label={label} name={name} wrapperCol={wrapperCol}>
      <TranslatableInput
        placeholder={label}
        disabled={!hasWritePermission || disabled}
        multiline={multiline}
        maxLength={maxLength}
      />
    </Form.Item>
  )

  const getSwitchFormItem = (name, label, onChange) => (
    <Form.Item
      label={label}
      name={name}
      valuePropName="checked"
    >
      <Switch disabled={!hasWritePermission} onChange={onChange} />
    </Form.Item>
  )

  const getFormSubmitButton = (disabled = false) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Button disabled={!hasWritePermission || disabled} type="primary" htmlType="submit">
          {t('common.submit')}
        </Button>
      </div>
    );
  }

  const getDocumentUploadFormItem = (name, disabledAdd, storageFolder, fileList, setFileList) => (
    <Form.Item
      name={name}
      noStyle
    >
      <div style={{ width: '100%' }}>
        <DocumentUploader
          disabledAdd={!hasWritePermission || disabledAdd}
          storageFolder={storageFolder}
          fileList={fileList}
          setFileList={setFileList}
          multiple={true}
          maxImageSize={15}
        />
      </div>
    </Form.Item>
  );

  const getOtherDimensionsFormItems = (initialDimensions, disabled = false) => {
    const [otherDimensionFields, setOtherDimensionFields] = useState([]);
    const [otherDimensionIds, setOtherDimensionIds] = useState([]);

    useEffect(() => {
      if (initialDimensions) {
        onAddOtherDimensions(Object.keys(initialDimensions));
        setOtherDimensionIds(Object.keys(initialDimensions).map(key => parseInt(key, 10)));
      }
    }, [initialDimensions])

    const onAddOtherDimensions = (idList) => {
      setOtherDimensionIds(idList);
      setOtherDimensionFields(idList.map(id => {
        return dropdowns.otherDimensions
          .find(dimension => dimension.id.toString() === id.toString());
      }));
    };

    return (
      <>
        {
          otherDimensionFields && otherDimensionFields.map(field => (
            <Form.Item
              key={field.id}
              name={['otherDimensions', `${field.id}`]}
              label={getLocalizedTitle(field)}
            >
              <Input disabled={!hasWritePermission || disabled} />
            </Form.Item>
          ))
        }
        <Select
          value={otherDimensionIds}
          placeholder={t('formBuilder.addAnotherDimension')}
          onChange={value => onAddOtherDimensions(value)}
          mode="multiple"
          allowClear
          style={{ width: '50%' }}
          suffixIcon={<PlusCircleOutlined />}
          disabled={!hasWritePermission || disabled}
        >
          {
            getDropdownOptions('otherDimensions')?.map(option => (
              <Option value={option.id} key={option.id}>{getLocalizedTitle(option)}</Option>
            ))
          }
        </Select>
      </>
    )
  }

  return {
    getDropdownOptions,
    getSelectFormItem,
    getDropdownFormItem,
    getMultiselectFormItem,
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getImageListFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getTranslatableFormItem,
    getSwitchFormItem,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
    getParentSelectFormItem,
    getChildSelectFormItem,
    getDocumentUploadFormItem,
    getTextAreaFormItemWoLength,
  }
}

